import { SessionTypes, SessionEvents, ParticipantTypes } from "./types"
import { handlePeerNegotiation, sendNegotiationOffer, attachPeerConnectionDiagnosticListeners } from "./shared.js"
import { getLogger } from "@/lib/logger"

const logger = getLogger("webrtc:participant")

export class Participant {
  #eventListeners = new Map()
  #customLocalMediaStream = null

  /**
   * 
   * @param {string} peerId - the id of the peer
   * @param {RTCPeerConnection} rtcPeerConnection - the RTCPeerConnection object
   * @param {ConnectionContext} context - the connection context
   * @param {any} dataChannelMessageListener - a callback for data channel messages. if not set, defaults to peer-to-peer listener
   */
  constructor({ peerId, rtcPeerConnection, context, participantType, dataChannelMessageListener, displayName }) {
    this.peerId = peerId
    this.rtcPeerConnection = rtcPeerConnection
    this.context = context
    this.participantType = participantType
    this.displayName = displayName
    this.mediaStream = null
    this.dataChannel = null
    this.dataChannelMessageListener = dataChannelMessageListener || this.onDataChannelMessage.bind(this)
    this.localIceCandidates = []
    this.makingNegotiationOffer = false
    this.screenShareTrackSenders = []  // RTPSenders from addTrack (video + optional screen-share audio); used by WebRTCSession
    this.screenShareMediaStreamId = null

    if (!this.peerId) {
      throw new Error("peerId is required")
    }
    if (!this.rtcPeerConnection) {
      throw new Error("rtcPeerConnection is required")
    }
    if (!this.context) {
      throw new Error("context is required")
    }
    if (!this.participantType) {
      throw new Error("participantType is required")
    }
  }

  // for backwards compatibility with UI
  get participantId() {
    return String(this.peerId);
  }

  /**
   * Mutes this participant's incoming audio (outgoing audio is uneffected)
   */
  muteAudio() {
    this.setAudioEnabled(false);
  }

  /**
   * Unmutes this participant's incoming audio (outgoing audio is uneffected)
   */
  unmuteAudio() {
    this.setAudioEnabled(true);
  }

  setAudioEnabled(isEnabled) {
    if (this.mediaStream) {
      this.mediaStream.getAudioTracks().forEach((trk) => {
        trk.enabled = isEnabled;
      });
    }
  }

  get isPatient() {
    return this.participantType === ParticipantTypes.device;
  }

  /**
   * Adds an event callback
   * @param {string} eventType The type of event
   * @param {string} eventCallback A callback
   */
  on(eventType, eventCallback) {
    if (this.#eventListeners.has(eventType)) {
      logger.warn('listener already registered, replacing', { peer_id: this.peerId, event: eventType });
    }
    this.#eventListeners.set(eventType, eventCallback);
  }

  emit(eventType, ...eventArgs) {
    const callback = this.#eventListeners.get(eventType);
    if (callback) {
      // unwrap varargs and call callback
      callback.apply(null, eventArgs);
    }
  }

  addLocalMediaTrack(track, stream) {
    this.rtcPeerConnection.addTrack(track, stream);
    logger.debug('local track added', { peer_id: this.peerId, kind: track.kind });
  }

  async connectLocalMedia() {
    const localMediaStream = this.#customLocalMediaStream ? this.#customLocalMediaStream : this.context.localMediaStream;
    if (localMediaStream) {
      logger.debug('connecting local media', { peer_id: this.peerId, connection_state: this.rtcPeerConnection.connectionState, signaling_state: this.rtcPeerConnection.signalingState });
      localMediaStream.getTracks().forEach((track) => {
        this.addLocalMediaTrack(track, localMediaStream);
      });
    } else if (this.context.sessionType !== SessionTypes.test) {
      logger.error('no local media stream available', { peer_id: this.peerId });
      this.emit("error", "no-local-media-stream");
    }
  }

  collectIceCandidates(maxWaitTimeMs = 1500) {
    this.localIceCandidates = [];
    this.rtcPeerConnection.addEventListener('icecandidate', (event) => {
      if (event.candidate) {
        this.localIceCandidates.push(event.candidate);
        logger.debug('local ice candidate added', { peer_id: this.peerId, candidate: event.candidate.candidate });
      }
    });

    if (this.rtcPeerConnection.iceGatheringState === 'complete') {
      return Promise.resolve(this.localIceCandidates);
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        logger.debug('ice candidate collection timed out', { peer_id: this.peerId, count: this.localIceCandidates.length });
        resolve(this.localIceCandidates);
      }, maxWaitTimeMs);
      this.rtcPeerConnection.addEventListener('icegatheringstatechange', () => {
        if (this.rtcPeerConnection.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          logger.debug('ice candidate collection complete', { peer_id: this.peerId, count: this.localIceCandidates.length });
          resolve(this.localIceCandidates);
        }
      });
    });
  }

  /**
   * Logging-only lifecycle listeners for a data channel. A close while the channel is still
   * this participant's active channel is unexpected (disconnect() drops the reference before
   * closing), so it warns; a close after deliberate teardown logs at debug.
   */
  #attachDataChannelLifecycleListeners(channel) {
    channel.addEventListener("open", () => {
      logger.info('data channel open', { peer_id: this.peerId });
    })
    channel.addEventListener("error", (event) => {
      logger.error('data channel error', { peer_id: this.peerId }, event.error ?? event);
    })
    channel.addEventListener("close", () => {
      if (this.dataChannel === channel) {
        logger.warn('data channel closed unexpectedly', { peer_id: this.peerId, connection_state: this.rtcPeerConnection?.connectionState });
      } else {
        logger.debug('data channel closed after teardown', { peer_id: this.peerId });
      }
    })
  }

  async onDataChannelMessage(payload) {
    switch (payload.type) {
      case "get-display-info": {
        // a browser peer is requesting this user's display name
        const displayName = this.context.options.userDisplayName;
        this.dataChannel.send(JSON.stringify({
          "type": "display-info-answer",
          displayName,
        }));
        logger.debug('display info sent', { peer_id: this.peerId });
        break;
      }
      case "user-bye":
        // assuming the other peer disconnection logic can handle this
        logger.info('user bye received', { peer_id: payload.peer_id });
        break;
      case "screen-share-start":
        // the event alerts that a screen share is starting and provides the media stream id so
        // that the receiving peer can identify the screen share track in the incoming media stream
        logger.info('screen share start received', { peer_id: payload.fromPeerId, stream_id: payload.screenShareMediaStreamId });
        this.screenShareMediaStreamId = payload.screenShareMediaStreamId;
        break;
      case "screen-share-stop":
        logger.info('screen share stop received', { peer_id: payload.fromPeerId });
        this.screenShareMediaStreamId = null;
        this.emit("session-event", SessionEvents.screenShareStopped, this);
        break;
      case "peer-negotiation":
        logger.debug('peer negotiation received', { peer_id: payload.fromPeerId });
        try {
          await handlePeerNegotiation({
            rtcPeerConnection: this.rtcPeerConnection,
            dataChannel: this.dataChannel,
            localPeerId: this.peerId,
            remotePeerId: payload.fromPeerId,
            makingOffer: this.makingNegotiationOffer,
            sdp: payload.sdp,
          });
        } catch (err) {
          logger.error('failed to handle peer negotiation', { peer_id: payload.fromPeerId }, err);
        }
        break;
      default:
        logger.warn('unexpected data channel payload type', { type: payload.type });
    }
  }

  configurePeerConnectionListeners() {
    const peerConn = this.rtcPeerConnection;
    const self = this;
    peerConn.addEventListener('track', (event) => {
      logger.debug('remote track received', { peer_id: this.peerId, kind: event.track?.kind, stream_id: event.streams[0]?.id });
      // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/track_event
      if (this.screenShareMediaStreamId && event.streams[0].id === this.screenShareMediaStreamId) {
        logger.debug('screen share track received', { peer_id: this.peerId, stream_id: this.screenShareMediaStreamId });
        this.emit("session-event", SessionEvents.participantScreenShareTrackAdded, self, event.streams[0]);
        return;
      }
      this.mediaStream = event.streams[0];
      this.emit("session-event", SessionEvents.participantTrackAdded, self);
    })

    attachPeerConnectionDiagnosticListeners(peerConn, this.peerId);
    peerConn.addEventListener('negotiationneeded', async () => {
      logger.debug('negotiation needed', { peer_id: this.peerId });
      this.makingNegotiationOffer = true;
      try {
        await sendNegotiationOffer({
          rtcPeerConnection: this.rtcPeerConnection,
          dataChannel: this.dataChannel,
          localPeerId: this.peerId,
        });
      } finally {
        this.makingNegotiationOffer = false;
      }
    });

    peerConn.addEventListener('connectionstatechange', () => {
      logger.debug('peer connection state changed', { peer_id: this.peerId, state: peerConn.connectionState });
      if (peerConn.connectionState === 'disconnected' || peerConn.connectionState === 'failed') {
        this.emit("disconnect", this);
      }
    })

    // for data channel to the video device, the channel is used to coordinate with other peers in a meeting.
    // data channels for peer-to-peer connections may also be setup for direct communication.
    // for example, the kiosk viewer uses a data channel to ask for the display name of the user
    peerConn.addEventListener("datachannel", (event) => {
      logger.debug('data channel added', { peer_id: this.peerId, label: event.channel?.label });
      this.dataChannel = event.channel;
      // add channel event listeners
      this.#attachDataChannelLifecycleListeners(this.dataChannel);
      this.dataChannel.addEventListener("message", async (event) => {
        logger.debug('data channel message received', { peer_id: this.peerId });
        const payload = JSON.parse(event.data);
        await this.dataChannelMessageListener(payload);
      })
    })
  }

  async configureRemote(remoteSdp, remoteIceCandidates, customLocalMediaStream = null) {
    this.#customLocalMediaStream = customLocalMediaStream;
    const peerConn = this.rtcPeerConnection;
    // let expectedRemoteTracks = 0;

    if (!remoteSdp) {
      throw new Error("no remote SDP provided");
    }
    logger.debug('configuring remote description', { peer_id: this.peerId, connection_state: peerConn.connectionState, signaling_state: peerConn.signalingState });
    this.configurePeerConnectionListeners();

    await peerConn.setRemoteDescription(remoteSdp);

    if (remoteIceCandidates) {
      remoteIceCandidates.forEach((cand) => {
        this.rtcPeerConnection.addIceCandidate(cand);
        logger.debug('remote ice candidate added', { peer_id: this.peerId, candidate: cand?.candidate });
      });
    }

    logger.debug('connecting local media after remote description', { peer_id: this.peerId });
    await this.connectLocalMedia();
  }

  async initializePeerConnection() {
    logger.debug('creating offer', { peer_id: this.peerId });
    // NOTE: assume caller is in charge of ice collection
    this.configurePeerConnectionListeners();
    this.dataChannel = this.rtcPeerConnection.createDataChannel("browser-peer")
    this.#attachDataChannelLifecycleListeners(this.dataChannel)
    this.dataChannel.addEventListener("message", async (event) => {
      logger.debug('data channel message received', { peer_id: this.peerId });
      const payload = JSON.parse(event.data);
      await this.dataChannelMessageListener(payload);
    })
    await this.connectLocalMedia();

    // Ensure the offer includes a video m= line even when not sending video,
    // so the answerer can send their video in the initial answer.
    // Without this, the answerer's addTrack(videoTrack) creates an unmatched
    // transceiver that can't be included in the answer, and the subsequent
    // negotiationneeded fires before the data channel is open, leaving the
    // negotiation-needed flag stuck and blocking future renegotiations
    // (e.g. screen share).
    const hasVideoSender = this.rtcPeerConnection.getSenders().some(s => s.track?.kind === 'video');
    if (!hasVideoSender) {
      this.rtcPeerConnection.addTransceiver('video', { direction: 'recvonly' });
    }
  }

  /**
   * Disconnects the participant's resources
   */
  disconnect() {
    logger.info('disconnecting participant', { peer_id: this.peerId });
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      try {
        this.dataChannel.send(JSON.stringify({
          peer_id: this.peerId,  // send the peer id of the participant that is disconnecting
          type: 'user-bye'
        }));
        logger.debug('user bye sent', { peer_id: this.peerId });
      } catch (err) {
        logger.warn('failed to send user bye', { peer_id: this.peerId }, err);
      }

      // close after a slight delay to make sure message goes through
      const channel = this.dataChannel;
      setTimeout(() => {
        channel.close();
        logger.debug('data channel closed', { peer_id: this.peerId });
      }, 100);
    } else {
      logger.debug('data channel not open at disconnect', { peer_id: this.peerId });
    }
    // drop the channel reference: its message listener's closure scope captures the peer
    // connection, so a retained participant would otherwise pin the closed PC wrapper
    // (which counts against Chrome's per-renderer PeerConnection cap) via that chain
    this.dataChannel = null;
    if (this.mediaStream) {
      this.mediaStream.onremovetrack = null;
      this.mediaStream.getTracks().forEach((trk) => {
        trk.stop();
        logger.debug('remote track stopped', { peer_id: this.peerId, kind: trk.kind, track_id: trk.id });
      });
      // "kiosk-viewer" for basic_mode; device-kiosk- for pusher
    } else if (this.peerId !== 'kiosk-viewer' && !this.peerId.startsWith('device-kiosk')) {  // kiosk-viewer is a special participant that doesn't send a media stream
      logger.warn('participant had no media stream at disconnect', { peer_id: this.peerId });
    }
    if (this.#customLocalMediaStream) {
      logger.debug('stopping custom local stream', { peer_id: this.peerId, stream_id: this.#customLocalMediaStream.id });
      this.#customLocalMediaStream.getTracks().forEach((track) => {
        track.stop();
        logger.debug('custom local track stopped', { kind: track.kind, track_id: track.id });
      });
      this.#customLocalMediaStream = null;
    } else {
      logger.debug('participant had no custom local stream at disconnect', { peer_id: this.peerId });
    }
    if (this.rtcPeerConnection) {
      // clear per-track handlers (e.g. the freeze detection in VideoDeviceParticipant): a track
      // kept alive natively (a detached <video> still rendering it) would otherwise retain this
      // participant through the handler closures, pinning the closed PC wrapper with it
      this.rtcPeerConnection.getReceivers().forEach((receiver) => {
        if (receiver.track) {
          receiver.track.onmute = null;
          receiver.track.onunmute = null;
          receiver.track.onended = null;
        }
      });
      this.rtcPeerConnection.close();
      logger.debug('peer connection closed', { peer_id: this.peerId });
    } else {
      logger.warn('participant had no peer connection at disconnect', { peer_id: this.peerId });
    }
    this.mediaStream = null;
    this.rtcPeerConnection = null;
    this.#eventListeners.clear();
    this.localIceCandidates = [];
  }
}